import { VerificationData } from '@/types'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import Verifications from '../Verifications'
import Login from '../Login'
import { useCartDispatch } from '@/contexts/CartContext'
import { LoginSchema, LoginValues } from '@/forms/Login'
import useAuth from '@/hooks/useAuth'
import cart from 'cart'
import { Form, Formik, FormikHelpers } from 'formik'
import SubmitButton from '@/components/forms/SubmitButton'
import TextInput from '@/components/forms/TextInput'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

interface LogInViewProps {
    currentView: string
    headingNumber: string | null
    shouldVerify: boolean
    setShouldVerify: React.Dispatch<React.SetStateAction<boolean>>
    verificationData: VerificationData | undefined
    setVerificationData: React.Dispatch<React.SetStateAction<VerificationData | undefined>>
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    VerificationError: any
    setVerificaitonError: React.Dispatch<React.SetStateAction<any>>
    handleVerification: any
    setVerificationError: any
    isOpen: boolean
    setCurrentView: any
    postLoginCallBack: any
    setPortLoginCallBack: any
    productType: any
    setProductType: any
    executePostLoginCallback: () => void
    onButtonClick: (e: React.MouseEvent, action: () => void) => void // Add this prop
    handleVerificationWhatsapp: any
}

const LogInView = (
    {
        currentView,
        headingNumber,
        verificationData,
        shouldVerify,
        setShouldVerify,
        setIsOpen,
        setVerificationData,
        VerificationError,
        setVerificaitonError,
        handleVerification,
        setVerificationError,
        isOpen,
        setCurrentView,
        postLoginCallBack,
        setPortLoginCallBack,
        productType,
        setProductType,
        executePostLoginCallback,
        onButtonClick, // Add this prop
        handleVerificationWhatsapp
    }: LogInViewProps
) => {
  const { login, isLoading } = useAuth('none')
  const {t: tCommon} = useTranslation('common');
  const {t: tAlerts} = useTranslation('alerts')

const handleSubmit = (
    values: LoginValues,
    actions: FormikHelpers<LoginValues>,
  ) => {
    login(
      values.identifier,
      values.password,
      actions.setFieldError,
      () => {
        setIsOpen(false);
        setCurrentView('login');
        setProductType(null);
        setPortLoginCallBack(undefined)
      },
      setVerificationError,
      setVerificationData,
      setProductType,
      tCommon,
      tAlerts
    )
  }
  const handleKeyPress = (e: React.KeyboardEvent, submitForm: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default form submission behavior
      submitForm();
    }
  }

  const {locale} = useRouter();
  return (
    <div className="modal-content p-3 lg:p-6 xl:p-8 2xl:p-11 pt-4 lg:pt-4 xl:pt-4 2xl:pt-4 pb-0 lg:pb-0 xl:pb-0 2xl:pb-0 " id='login-view'>
      <AnimatePresence>
        <motion.div
          initial={{opacity: 0, translateX: '10%'}}
          transition={{type: 'spring', duration: 0.5}}
          animate={{opacity: 1, translateX: '0'}}
          exit={{opacity: 0, translateX: '10%'}}>
          {/*
           * In Case the User is Not Verified Show this
           */}
          {verificationData && shouldVerify ? (
            <Verifications
              phone={verificationData.phone ?? ''}
              email={verificationData.email}
              fromRegister={false}
              setShouldVerify={setShouldVerify}
              setVerificationData={setVerificationData}
              setIsOpen={setIsOpen}
              postLoginCallBack={postLoginCallBack}
              setPortLoginCallBack={setPortLoginCallBack}
              productType={productType}
              setProductType={setProductType}
              executePostLoginCallback={executePostLoginCallback}
              setCurrentView={setCurrentView}
            />
          ) : (
            <>
              <AnimatePresence>
                <motion.div
                  initial={{opacity: 0, translateX: '10%'}}
                  transition={{type: 'spring', duration: 0.5}}
                  animate={{opacity: 1, translateX: '0'}}
                  exit={{opacity: 0, translateX: '10%'}}>
                  <>
                    {VerificationError && (
                      <div
                        className="bg-red border border-red text-white px-4 py-3 rounded relative mb-3"
                        role="alert">
                        <strong className="font-bold">
                          {tCommon('loginError')}
                        </strong>
                        <span className="block sm:inline mr-3 text-sm">
                          <span>
                            <button
                              onClick={e => onButtonClick(e, () => {
                                setVerificaitonError(false)
                                handleVerificationWhatsapp()
                                setShouldVerify(true)
                              })}
                              className="text-primary font-semibold underline">
                              {tCommon('changeLoginDetails')}
                            </button>
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={e => onButtonClick(e, () => {
                            setVerificationError(false)
                          })}
                          className="absolute top-0 bottom-0 right-0 px-4 py-3">
                          <svg
                            className="fill-current h-6 w-6 text-red-500"
                            role="button"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20">
                            <title>Close</title>
                            <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <Formik
                      initialValues={{
                        identifier: '',
                        password: '',
                      }}
                      validateOnChange={false}
                      validateOnBlur={false}
                      validationSchema={LoginSchema}
                      onSubmit={(values, actions) => {
                        handleSubmit(values, actions);
                      }}>
                      {({ errors, touched, submitForm }) => (
                        <Form>
                          <div className="form">
                            <div className="form-group flex flex-col">
                              <TextInput
                                label={`${tCommon('phoneOrEmail')}`}
                                type="text"
                                name="identifier"
                                placeholder={`${tCommon('phoneOrEmailPlaceholder')}`}
                                dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                                isError={errors.identifier && touched.identifier}
                                error={errors.identifier}
                              />
                            </div>
                            <div className="form-group flex flex-col mt-3">
                              <div className="relative w-full">
                                <TextInput
                                  label={`${tCommon('password')}`}
                                  type="password"
                                  name="password"
                                  placeholder={`${tCommon('passwordPlaceholder')}`}
                                  isError={errors.password && touched.password}
                                  error={errors.password}
                                  dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                                />
                              </div>
                            </div>
                
                            <div className="flex items-center justify-between mt-3">
                              <div className="checkbox opacity-0 flex items-center">
                                <input
                                  type="checkbox"
                                  name="checkbox"
                                  className="rounded
                                                bg-white
                                        border-primary-500
                                        focus:border-transparent focus:bg-gray-200
                                        ml-2
                                        text-primary-500
                                        focus:ring-1 focus:ring-gray-700"
                                  id="login-checkbox"
                                  value={'1'}
                                />
                                <label
                                  htmlFor="login-checkbox"
                                  className="text-xs border-primary font-bold ml-1">
                                  {tCommon('remember')}
                                </label>
                              </div>
                              <button
                                type="button"
                                onClick={e => onButtonClick(e, () => {
                                  setCurrentView('forgot')
                                })}
                                id='forgot-password-button'
                                className="font-semibold underline text-sm cursor-pointer"
                                dir={locale === 'ar' ? 'rtl' : 'ltr'}
                                >
                                {tCommon('forgotPasswordText')}
                              </button>
                            </div>
                            <SubmitButton isLoading={isLoading} text={`${tCommon('enter')}`} textType="string"/>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </>
                </motion.div>
              </AnimatePresence>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default LogInView;